import { CSSProperties, useCallback, useRef } from "react";
import { DocumentNode } from "@apollo/client";

import Table from "components/Table";
import TableContextProvider from "components/Table/Context";
import TableVirtualizedSpacerRow from "components/Table/VirtualizedSpacerRow";
import TableRow from "components/Table/Row";
import useTableSort from "components/Table/useTableSort";
import TableWrapper from "components/Table/Wrapper";
import EmptyState from "ds/components/EmptyState";
import { EmptystateSearchNoResultsColored } from "components/icons/generated";
import { ApiKey } from "types/generated";

import {
  ANALYTICS,
  INITIAL_COLUMNS_VIEW_CONFIG,
  initialSortDirection,
  initialSortOption,
  ROW_HEADER_ID,
  SORT_CONFIG,
  API_KEYS_COLUMN_CONFIG,
  API_KEYS_CUSTOM_END_COLUMN_CONFIG,
  API_KEYS_FILTERS_ORDER_SETTINGS_KEY,
  API_KEYS_COLUMNS_STORAGE_KEY,
} from "./constants";
import ApiKeysListItem from "./Item";
import { useFilteredApiKeys } from "./useFilteredApiKeys";

type ApiKeysListProps = {
  apiKeys: ApiKey[];
  refetchQueriesOnDelete?: DocumentNode[];
  refetchQueriesOnUpdate?: DocumentNode[];
};

const ApiKeysList = ({
  apiKeys,
  refetchQueriesOnDelete,
  refetchQueriesOnUpdate,
}: ApiKeysListProps) => {
  const virtualizedListContainerRef = useRef<HTMLDivElement | null>(null);
  const { handleSortOptionChange, sortDescriptor } = useTableSort({
    filtersOrderSettingsKey: API_KEYS_FILTERS_ORDER_SETTINGS_KEY,
    sortConfig: SORT_CONFIG,
    initialSortOption,
    initialSortDirection,
  });

  const filteredApiKeys = useFilteredApiKeys(apiKeys, sortDescriptor);

  const renderRow = useCallback(
    (
      item: Partial<ApiKey> & {
        virtIndex?: number;
        style?: CSSProperties;
        virtKey?: string;
        id: string;
        height?: number;
        ref?: (node: Element | null | undefined) => void;
      }
    ) => {
      if ("height" in item && (item.id === "after" || item.id === "before")) {
        return <TableVirtualizedSpacerRow height={item.height} />;
      }

      return (
        <TableRow
          index={item.virtIndex}
          ref={item.ref}
          style={item.style}
          key={item.virtKey}
          id={item.id}
          name={item.name}
        >
          <ApiKeysListItem
            apiKey={item as ApiKey}
            refetchQueriesOnDelete={refetchQueriesOnDelete}
            refetchQueriesOnUpdate={refetchQueriesOnUpdate}
          />
        </TableRow>
      );
    },
    [refetchQueriesOnDelete, refetchQueriesOnUpdate]
  );

  return (
    <TableContextProvider
      columnsConfig={API_KEYS_COLUMN_CONFIG}
      endCustomColumns={API_KEYS_CUSTOM_END_COLUMN_CONFIG}
      initialColumnViewConfig={INITIAL_COLUMNS_VIEW_CONFIG}
      rowHeaderId={ROW_HEADER_ID}
      localStorageId={API_KEYS_COLUMNS_STORAGE_KEY}
      setSortDescriptor={handleSortOptionChange}
      sortDescriptor={sortDescriptor}
      {...ANALYTICS}
    >
      <TableWrapper virtualizedListContainerRef={virtualizedListContainerRef}>
        {!!filteredApiKeys?.length && (
          <Table
            ariaLabel="API keys list"
            items={filteredApiKeys}
            ref={virtualizedListContainerRef}
          >
            {renderRow}
          </Table>
        )}
        {!filteredApiKeys.length && !!apiKeys?.length && (
          <EmptyState title="No results" icon={EmptystateSearchNoResultsColored} announce />
        )}
      </TableWrapper>
    </TableContextProvider>
  );
};

export default ApiKeysList;
